<script lang="ts">
  import { onMount } from "svelte"
  import BiggerPicture from "bigger-picture"
  import { fade } from "svelte/transition"
  import { BarLoader } from "svelte-loading-spinners"
  import PostComment from "./PostComment.svelte"
  import Honeybadger from "$app/utils/honeybadger"
  import {
    MessageSquareText,
    EllipsisVertical,
    Pencil,
    Trash2,
    Pin,
    BookText
  } from "lucide-svelte"
  import Dropdown from "$app/components/Dropdown.svelte"
  import Tiptap from "$app/components/Tiptap.svelte"
  import {
    xhr,
    timeAgo,
    toast,
    wait,
    queryToUrl,
    formatDateTime
  } from "$app/utils"
  import { emojis } from "$app/constants"
  import { deleteFileFromList, uploadFile } from "./utils"
  import Reaction from "$app/components/Reaction.svelte"
  import { appStore } from "$app/store.svelte"
  import "bigger-picture/css"

  interface Props {
    post: ForumPostType & { plan?: PlanType }
    showPinned?: boolean
    readOnly?: boolean
    showLink?: boolean
    showComments?: boolean
    onopenpost?: () => void
    closeposts?: () => void
    ondelete?: () => void
    onupdate?: (post: ForumPostType) => void
  }

  let {
    post = $bindable(),
    showPinned = true,
    readOnly = false,
    showLink = false,
    showComments = $bindable(false),
    onopenpost = () => {},
    closeposts = () => {},
    ondelete = () => {},
    onupdate = () => {}
  }: Props = $props()

  let postUserId = $state(post.user_id)
  let scheduledTime = new Date(post.published_at_utc)
  let schedulePostValue = $state(scheduledTime.toISOString().slice(0, 16))
  let schedulePost: Boolean = $state(false)

  let openDropdown = $state(false)
  let editPost = $state(false)
  let showAddComment = $state(false)
  let reaction: ReactionType
  let isLiked = false

  let savingPost = $state(false)

  const gap = 0
  const defaultDirection = "end"
  const rowRange = 0
  const columnRange = [1, 8]
  const sizeRange = [140, 600]
  const isCroppedSize = false
  const displayedRow = -1

  onMount(() => {
    schedulePost = schedulePostValue && schedulePostValue.length > 0

    if (post.reactions.length > 0) {
      let myReaction = post.reactions.filter(
        (r) => r.user_id === appStore.current_user.id
      )
      if (myReaction.length > 0) {
        isLiked = true
        reaction = myReaction[0]
      }
    }

    loadCommentReactions()
    initGallery()
  })

  function initGallery() {
    if (post.files.length > 0) {
      let bp = BiggerPicture({
        target: document.body
      })

      // grab image links
      let imageLinks = document.querySelectorAll(`a.lightbox-image-${post.id}`)

      // add click listener to open BiggerPicture
      for (let link of imageLinks) {
        link.addEventListener("click", openGallery)
      }

      // function to open BiggerPicture
      function openGallery(e: MouseEvent) {
        e.preventDefault()
        bp.open({
          items: imageLinks,
          el: e.currentTarget
        })
      }
    }
  }

  async function updatePost(updated_post: ForumPostType): Promise<void> {
    savingPost = true

    if (appStore.plan.id === 1) {
      updated_post.user_id = postUserId
      if (!updated_post.is_published) {
        updated_post.published_at = schedulePost
          ? schedulePostValue
          : updated_post.created_at
      }
    }

    try {
      const resp = await xhr.put<ForumPostType>(
        `/plans/${appStore.plan.id}/community_posts/${post.id}.json`,
        {
          forum_post: updated_post
        }
      )
      savingPost = false
      const updatedPost = resp.data

      const idx = appStore.channelPosts.findIndex((p) => post.id === p.id)
      appStore.channelPosts[idx] = updatedPost
      onupdate(post)
      editPost = false
    } catch (e: any) {
      Honeybadger.notify(e)
    } finally {
      editPost = false
      savingPost = false
      initGallery()
    }
  }

  function savePost(content: string) {
    if (content.length > 0) {
      updatePost({ ...post, content })
    }
  }

  let selected = $state(false)

  async function deletePost(): Promise<void> {
    selected = true
    await wait(30)
    if (!confirm(`Are you sure you want to delete this post?`)) {
      selected = false
      return
    }

    try {
      await xhr.delete<ForumPostType>(
        `/plans/${appStore.plan.id}/community_posts/${post.id}.json`
      )
      editPost = false

      appStore.channelPosts = appStore.channelPosts.filter(
        (p) => p.id !== post.id
      )
      ondelete()
    } catch (e: any) {
      if (e.response.data) {
        toast(e.response.data, "error")
      } else {
        toast("There was an error deleting the post", "error")
      }
    } finally {
      selected = false
    }
  }

  // Create comment

  let newComment = $state("")
  let savingComment = $state(false)
  async function createComment(comment: string): Promise<void> {
    savingComment = true
    try {
      const resp = await xhr.post<ForumPostCommentType>(
        `/plans/${appStore.plan.id}/community_comments.json`,
        {
          forum_post_comment: {
            forum_post_id: post.id,
            content: comment
          }
        }
      )

      post.comments = [resp.data, ...post.comments]
      const idx = appStore.channelPosts.findIndex((p) => p.id === post.id)
      appStore.channelPosts[idx] = post
    } catch (e: any) {
      Honeybadger.notify(e)
    } finally {
      savingComment = false
      newComment = ""
      showAddComment = false
      showComments = true
    }
  }

  function hasEmojiReactions(emoji: string): boolean {
    return post.reactions.filter((r) => r.emoji === emoji).length > 0
  }

  const reactionsByEmoji = function (emoji: string) {
    return post.reactions.filter((r) => r.emoji === emoji)
  }

  let uploadingFile = $state(false)
  async function addFile(file: File): Promise<void> {
    uploadingFile = true
    try {
      const resp = await uploadFile(file, {
        plan_id: appStore.plan.id,
        channel_id: appStore.current_channel.id,
        id: post.id,
        user_id: post.user_id
      })

      const idx = appStore.channelPosts.findIndex((p) => p.id === post.id)

      appStore.channelPosts[idx] = { ...post, ...resp }
    } catch (e: any) {
      toast("There was an error uploading your file", "error")
    } finally {
      uploadingFile = false
    }
  }

  async function deleteFile(file: ActiveStorageType): Promise<void> {
    file._selected = true
    post.files = post.files
    await wait(50)

    if (await deleteFileFromList(file, appStore.plan.id, post.id)) {
      file._selected = true
      post.files = post.files.filter((f) => f.id !== file.id)
      const idx = appStore.channelPosts.findIndex((p) => p.id === post.id)
      appStore.channelPosts[idx] = post
    } else {
      file._selected = false
      post.files = post.files
    }
  }

  function toggleAddComment() {
    showAddComment = !showAddComment
    showComments = showAddComment
  }

  function updateReaction(reaction: ReactionType) {
    currentReaction = reaction
    post.reactions = post.reactions.map((r) => {
      if (r.id === reaction.id) {
        return { ...r, ...reaction }
      }

      return r
    })
  }

  function removeReaction(reaction: ReactionType) {
    post.reactions = post.reactions.filter((r) => r.id !== reaction.id)

    currentReaction = { id: null, emoji: null } as ReactionType
  }
  let currentReaction: ReactionType = $state(
    post.reactions.find((r) => r.user_id === appStore.current_user.id) ||
      ({ id: null, emoji: null } as ReactionType)
  )

  const oddFiles = post.files?.length % 2 === 1

  let loadedCommentReactions = false
  async function loadCommentReactions() {
    const ids = post.comments.map((c) => c.id)

    const query = queryToUrl({
      reactionable_type: "RecognitionComment",
      reactionable_id: ids
    })

    try {
      const resp = await xhr.get(
        `/api/v1/plans/${post.plan_id}/reactions.json?${query}`
      )

      post.comments = post.comments.map((c) => {
        c.reactions = resp.data.filter((r) => r.reactionable_id === c.id)
        return c
      })

      loadedCommentReactions = true
    } catch (e: any) {
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
    }
  }
</script>

{#if post.is_published || post.user_id == appStore.current_user.id || appStore.plan.id === 1}
  <li
    class="bg-white shadow border rounded border-gray-100"
    class:border-red-800={selected}
    class:border-indigo-200={post.is_pinned && showPinned}
    class:border-amber-400={!post.is_published}
    transition:fade
    data-name="forum-post"
  >
    <article class="px-4 lg:px-5 py-6">
      <div>
        <div class="flex space-x-3">
          <div class="flex-shrink-0">
            <img
              class="h-10 w-10 rounded-xl shadow-lg"
              src={post.user.avatar_thumb_url}
              alt={post.user.name}
            />
          </div>
          <div class="min-w-0 flex-1">
            <p class="text-sm font-medium text-gray-900">
              {post.user.name}
              {#if post.is_published}
                <span class="ml-2 text-sm text-gray-700 font-medium">
                  {timeAgo(post.published_at)}
                </span>
              {:else}
                <span
                  class="ml-2 text-base text-amber-700 font-medium border border-amber-600 p-1"
                >
                  Publish Pending: {formatDateTime(post.published_at)}
                </span>
              {/if}
            </p>
            <p class="text-sm text-gray-500">
              {#if post.user.title}
                {post.user.title || ""}
                {#if post.plan && post.plan.company_name !== "null"}
                  <span class="text-xs ml-1">
                    @ {post.plan?.company_name || ""}
                  </span>
                {/if}
              {/if}
            </p>
          </div>
          <div class="flex items-center justify-end">
            {#if post.is_pinned && showPinned}
              <Pin
                class="mr-3 size-5 text-red-200"
                style="fill: currentColor"
              />
            {/if}
            <img
              src={appStore.plan.logo_url}
              class="w-6 h-6 rounded-full shadow"
              alt={appStore.plan.name}
            />
          </div>
          {#if (post.user_id == appStore.current_user.id || appStore.plan.id === 1) && !readOnly}
            <div class="flex flex-shrink-0 self-center">
              <Dropdown bind:open={openDropdown}>
                {#snippet button()}
                  <span class="text-gray-700 p-1" data-name="post-context-menu">
                    <EllipsisVertical class="text-gray-400 size-5" />
                  </span>
                {/snippet}

                <div>
                  <div
                    class="origin-top-right absolute right-0 mt-2 w-36 rounded-md shadow-lg bg-core border z-10"
                    role="menu"
                  >
                    <div class="py-1" role="none">
                      {#if showPinned && appStore.plan.id === 1}
                        <button
                          class="btn-dropdown"
                          role="menuitem"
                          onclick={() => {
                            openDropdown = false
                            updatePost({ ...post, is_pinned: !post.is_pinned })
                          }}
                        >
                          {#if post.is_pinned}
                            <Pin
                              class="text-gray-400 w-4 h-4 mr-3"
                              style="fill: currentColor"
                            />
                            Unpin
                          {:else}
                            <Pin class="text-gray-400 w-4 h-4 mr-3" />
                            Pin to Top
                          {/if}
                        </button>
                      {/if}

                      {#if post.user_id == appStore.current_user.id || appStore.plan.id === 1}
                        <button
                          class="btn-dropdown"
                          role="menuitem"
                          data-name="edit-post-button"
                          onclick={() => {
                            openDropdown = false
                            editPost = !editPost
                            editPost ? onopenpost() : closeposts()
                          }}
                        >
                          <Pencil class="text-gray-400 w-4 h-4 mr-3" />
                          {editPost ? "Stop editing" : "Edit"}
                        </button>
                        <button
                          class="btn-dropdown"
                          role="menuitem"
                          data-name="delete-post-button"
                          onclick={() => {
                            openDropdown = false
                            deletePost()
                          }}
                        >
                          <Trash2 class="text-gray-400 w-4 h-4 mr-3" />
                          Delete
                        </button>
                      {/if}
                      {#if showLink}
                        <button
                          class="btn-dropdown"
                          role="menuitem"
                          data-name="show-post-button"
                          onclick={() => {
                            window.location.href = `/plans/${post.plan_id}/community_posts/${post.id}`
                          }}
                        >
                          <BookText class="text-gray-400 size-4 mr-3" />
                          View Post
                        </button>
                      {/if}
                    </div>
                  </div>
                </div>
              </Dropdown>
            </div>
          {/if}
        </div>
      </div>
      <div class="space-y-4 text-base text-gray-800 pb-2 w-full z-0">
        {#if editPost}
          {#if appStore.plan.id === 1}
            <div class="grid grid-cols-3 gap-4 items-center pt-2 mb-2">
              <div class="flex items-center justify-end">
                <label for="user_id"> Post As: </label>
              </div>
              <div class="flex items-center col-span-2">
                <select
                  bind:value={postUserId}
                  id="user_id"
                  class="max-w-64 rounded-md text-sm border-0 py-1.5 pl-3 pr-10 text-gray-700 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-blue-600 sm:text-sm sm:leading-6"
                >
                  {#each appStore.users as user (user.id)}
                    <option value={user.id} class="text-sm">
                      {user.name}
                    </option>
                  {/each}
                </select>
              </div>
              {#if !post.is_published}
                <div class="flex items-center justify-end h-8">
                  <label for="schedulePost"> Schedule Post: </label>
                  <input
                    bind:checked={schedulePost}
                    id="schedulePost"
                    type="checkbox"
                    class="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600 ml-2"
                  />
                </div>
                <div class="flex items-center justify-start col-span-2">
                  {#if schedulePost}
                    <input
                      type="datetime-local"
                      bind:value={schedulePostValue}
                      class="max-w-72 rounded-md text-sm border-0 py-1.5 pl-3 text-gray-700 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-blue-600 sm:text-sm sm:leading-6"
                    />
                    <span class="font-bold text-sm ml-2">(UTC)</span>
                  {/if}
                </div>
              {/if}
            </div>
          {/if}
          <Tiptap
            id={`post-${post.id}`}
            content={post.content}
            saving={savingPost}
            onsave={({ html }) => savePost(html)}
            ondeletefile={({ detail }) => deleteFile(detail)}
            onfileupload={(file) => {
              addFile(file)
            }}
            buttons={[
              ["bold", "italic", "underline", "strike"],
              ["bulletList", "orderedList", "blockquote"],
              ["link", "unlink", "image"]
            ]}
            allowEmojis={true}
            showSaveArrow={true}
            allowGiphy={true}
            allowFileUploads={true}
            {uploadingFile}
            files={post?.files || []}
          >
            {#snippet bottom()}
              <button
                class="btn btn-sm secondary"
                onclick={() => {
                  editPost = false
                }}
              >
                Cancel
              </button>
            {/snippet}
          </Tiptap>
        {:else}
          <div class="ProseMirror mt-5">
            {@html post.content || ""}
          </div>
          <div id={`#images-${post.id}`} class="image-gallery">
            <div class="justified-grid">
              {#each post.files || [] as item, idx}
                <div class="image">
                  <a
                    href={item?.blob?.filename}
                    data-img={item?.blob?.url}
                    data-thumb={item?.blob?.url}
                    data-alt={item?.blob?.filename}
                    data-height={item?.blob?.metadata?.height}
                    data-width={item?.blob?.metadata?.width}
                    class="lightbox-image-{post.id}"
                  >
                    <img
                      src={item?.blob?.url}
                      alt={item?.blob?.filename}
                      class="hover:shadow-lg hover:border-2 hover:border-blue-400"
                      loading="lazy"
                    />
                  </a>
                </div>
              {/each}
            </div>
          </div>
        {/if}
      </div>
    </article>

    <div class="border-t border-gray-200">
      <div class="flex w-full items-center justify-between px-6 py-2">
        <div class="flex justify-start items-center">
          {#key post.reactions}
            {#each emojis as emoji}
              {#if hasEmojiReactions(emoji.name)}
                <img
                  src={emoji.url}
                  class="size-8 ml-3"
                  data-name="reaction-icon"
                  alt="Reaction {emoji.name}"
                />
                <div
                  class="flex -space-x-2 overflow-hidden"
                  data-name="reaction-users"
                >
                  {#each reactionsByEmoji(emoji.name) as reaction}
                    <img
                      class="inline-block size-6 rounded-full ring-2 ring-white"
                      src={reaction.user?.avatar_url}
                      alt="reaction"
                    />
                  {/each}
                </div>
              {/if}
            {/each}
          {/key}
        </div>
        <div>
          {#if post.comments.length >= 1}
            <button
              class="focus:outline-none hover:underline text-gray-700 hover:text-gray-600"
              data-name="comments-button"
              onclick={() => (showComments = !showComments)}
            >
              {#if post.comments.length > 1}
                {post.comments.length} Comments
              {:else if post.comments.length === 1}
                1 Comment
              {/if}
            </button>
          {:else}
            No Comments
          {/if}
        </div>
      </div>

      <!--Comments-->
      <div
        class="flex w-full items-center justify-center px-12 py-2 border-t border-gray-200"
      >
        <div class="flex gap-x-4">
          <Reaction
            reactionable={post}
            reactionable_type="ForumPost"
            reaction={currentReaction}
            css="w-48 hover:bg-gray-100 h-full py-2"
            onupdate={(data) => updateReaction(data)}
            oncreate={(data) => {
              post.reactions = [...post.reactions, data]
              currentReaction = data
            }}
            ondelete={(data) => removeReaction(data)}
          />

          {#if appStore.permission_level !== "none"}
            <button
              data-name="show-add-comment-button"
              onclick={toggleAddComment}
              class="text-gray-700 flex px-16 justify-end items-center hover:bg-gray-100 py-2 mr-5 text-base"
            >
              <MessageSquareText
                class="text-gray-400 h-5 w-5 mr-1 hover:text-gray-500"
              />
              <span class="font-medium text-base text-gray-600">Comment</span>
            </button>
          {/if}
        </div>
      </div>
      <!--end of Comments-->
      <!--Add comment-->
      {#if showAddComment}
        <div
          transition:fade
          class="py-2 px-5 bg-white border-t border-gray-100"
        >
          <div class="mb-6 flex">
            <div class="flex gap-x-2 flex-grow items-start">
              <div class="flex-shrink-0">
                <div class="relative">
                  <img
                    src={appStore.current_user.avatar_thumb_url}
                    class="mt-1 h-8 w-8 rounded-xl bg-gray-400 flex items-center justify-center"
                    alt={appStore.current_user.name}
                  />
                </div>
              </div>
              <div class="min-w-0 flex-1">
                <div class="z-0">
                  <label for="comment" class="sr-only">Comment</label>
                  {#if savingComment}
                    <div class="py-12 flex justify-center w-full">
                      <BarLoader
                        size="240"
                        color="#3b2f2b"
                        unit="px"
                        duration="1s"
                      />
                    </div>
                  {:else}
                    <Tiptap
                      id={`comment-${post.id}`}
                      placeholder="Share your thoughts …"
                      content={newComment}
                      saving={savingComment}
                      onsave={({ html }) => createComment(html)}
                      ondeletefile={(file) => deleteFile(file)}
                      onfileupload={(file) => addFile(file)}
                      buttons={[
                        ["bold", "italic", "underline", "strike"],
                        ["bulletList", "orderedList", "blockquote"],
                        ["link", "unlink", "image"]
                      ]}
                      allowEmojis={true}
                      showSaveArrow={true}
                      allowGiphy={true}
                    />
                  {/if}
                </div>
              </div>
            </div>
          </div>
        </div>
      {/if}
      <!--end of add comment-->

      {#if showComments}
        <div
          transition:fade
          class="py-6 px-4 bg-white border-t border-gray-100"
        >
          <div class="flow-root">
            <ul>
              {#each post.comments as comment (comment.id)}
                <PostComment {comment} />
              {/each}
            </ul>
          </div>
        </div>
      {/if}
    </div>
  </li>
{/if}
